import { useCallback, useEffect, useState } from "react";

import VaporController from "@abi/VaporController.json";
import useVaporControllerContract from "@hooks/useVaporControllerContract";
import type { MyLocale } from "@i18n";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useI18n } from "next-rosetta";
import type { Address } from "viem";
import { useCall, useSimulateContract, useWriteContract } from "wagmi";

const useRename = () => {
	const vaporControllerContract = useVaporControllerContract();
	const { data } = useSimulateContract({
		abi: VaporController,
		address: vaporControllerContract.address as Address,
		functionName: "renameNode",
	});

	const {
		isPending: isLoading,
		reset,
		status,
		writeContract,
	} = useWriteContract();
	const state = status;
	const write = useCallback(() => {
		if (data?.request) {
			return writeContract(data?.request);
		}
	}, [data]);
	return { isLoading, resetState: reset, send: write, state, status };
};

export default function RenameNode({ open, setOpen }) {
	const [nodeName, setNodeName] = useState("");
	const { isLoading, send: rename, status } = useRename();
	const i18n = useI18n<MyLocale>();

	const handleClose = () => {
		setOpen(false);
	};

	const handleNameChange = (event) => {
		setNodeName(event.target.value);
	};

	const handleRename = () => {
		rename();
	};

	useEffect(() => {
		if (status && status === "success") {
			setOpen(false);
		}
	}, [setOpen, status]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					backgroundImage: "unset",
					borderRadius: 5,
				},
			}}
		>
			<DialogTitle>{i18n.t("modals.rename.title")}</DialogTitle>
			<DialogContent sx={{ minWidth: 280 }}>
				<FormControl fullWidth sx={{ marginBottom: 2 }}>
					<TextField
						autoFocus
						margin="dense"
						id="name"
						label={i18n.t("modals.rename.label")}
						type="text"
						fullWidth
						variant="standard"
						value={nodeName}
						onChange={handleNameChange}
						required
					/>
				</FormControl>
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: "space-between",
					paddingBottom: 4,
					px: 4,
				}}
			>
				<Button onClick={handleClose} variant="outlined" color="error">
					{i18n.t("modals.rename.cancel")}
				</Button>
				<Tooltip title="">
					<Stack direction="row" spacing={2}>
						<LoadingButton
							variant="contained"
							color="primary"
							onClick={handleRename}
							disabled={!nodeName}
							loading={isLoading}
						>
							{i18n.t("modals.rename.rename")}
						</LoadingButton>
					</Stack>
				</Tooltip>
			</DialogActions>
		</Dialog>
	);
}
